import { DocumentationPopup, Loader, Tooltip } from 'components';
import React, {
  ChangeEvent,
  DragEvent,
  FC,
  KeyboardEvent,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {
  ControllerRenderProps,
  DeepMap,
  ErrorOption,
  FieldError,
  UseFormMethods,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  ComposerCheckIcon,
  ComposerCollapseIcon,
  ComposerDocumentIcon,
  ComposerExpandIcon,
  ComposerPaperclipIcon,
  ComposerSendIcon,
  ComposerUserPlusIcon,
  DeleteIcon,
} from 'static';
import {
  getCurrentCommunity,
  getCurrentUserInCommunity,
} from 'store/selectors/currentCommunity';
import { Document, MyAppointmentsEvent } from 'types';
import { hasCommunityOrGroupAdminPermissions } from 'utils';
import { getProfileName } from 'utils/common';

import {
  getEligibleCommunityRecipients,
  getMaxCommunityRecipients,
} from '../communityRecipients';
import {
  EmailModuleFormValues,
  FIELDS,
  getFieldError,
  MAX_EMAIL_ATTACHMENTS_BYTES,
} from '../form';
import { MAX_OTHER_RECIPIENTS, parseOtherRecipients } from '../otherRecipients';
import { GridGhostWrapper } from '../styled';
import { TURNSTILE_ENABLED, TurnstileHandle } from '../Turnstile';
import Attachments from './Attachments';
import EventBlock from './EventBlock';
import EmailPreview from './Preview';
import RecipientsField, { RecipientsFieldHandle } from './RecipientsField';
import RichTextEditor, {
  MOD_KEY_LABEL,
  RichTextEditorHandle,
} from './RichTextEditor';
import FormatToolbar from './RichTextEditor/formatToolbar';
import {
  StyledComposer,
  StyledComposerBody,
  StyledComposerHeader,
  StyledComposerHeaderActions,
  StyledComposerScrollContent,
  StyledComposerSection,
  StyledComposerTitle,
  StyledCopyCheck,
  StyledCopyToggle,
  StyledDiscardButton,
  StyledDropOverlay,
  StyledFooter,
  StyledFooterActions,
  StyledFooterTools,
  StyledHeaderIconButton,
  StyledHiddenFileInput,
  StyledIconButton,
  StyledInlineError,
  StyledSendButton,
  StyledSendButtonWrapper,
  StyledSheet,
  StyledSheetInset,
  StyledSubjectInput,
  StyledTurnstile,
} from './styled';

const MESSAGE_EDITOR_ID = 'email-composer-message';
const RECIPIENTS_INPUT_ID = 'email-composer-to';

interface EmailModuleMessageProps
  extends Pick<UseFormMethods<EmailModuleFormValues>, 'clearErrors'> {
  attachmentsFieldProps: ControllerRenderProps<EmailModuleFormValues>;
  otherRecipientsFieldProps: ControllerRenderProps<EmailModuleFormValues>;
  recipientsFieldProps: ControllerRenderProps<EmailModuleFormValues>;
  messageFieldProps: ControllerRenderProps<EmailModuleFormValues>;
  subjectFieldProps: ControllerRenderProps<EmailModuleFormValues>;
  errors: DeepMap<EmailModuleFormValues, FieldError>;
  setError: (name: string, error: ErrorOption) => void;
  documentation: Document[];
  sending: boolean;
  onPrepareSend: () => Promise<boolean>;
  sendToMe: boolean;
  captchaToken: string | null;
  onCaptchaSuccess: (token: string) => void;
  onCaptchaExpire: () => void;
  turnstileRef: React.RefObject<TurnstileHandle>;
  onRemoveDocument: (document: { name: string }) => void;
  setSelectedParticipants: (data: string[]) => void;
  selectedRecipients: string[];
  otherRecipients: string;
  subject: string;
  outgoingBody: string;
  selectedEvent: MyAppointmentsEvent | null;
  eventInEmail: boolean;
  eventWhen: string;
  eventWhere: string;
  eventLink: string | null;
  eventLinkLoading: boolean;
  eventLinkFailed: boolean;
  onAddEventToEmail: () => void;
  onRemoveEventFromEmail: () => void;
  onDocumentsAttach: (documents: Document[]) => void;
  submitForm: () => Promise<boolean>;
  onDiscardDraft: () => void;
  onSendToMeChange: (checked: boolean) => void;
  handleOnWrapperClick: (e: MouseEvent) => void;
  fullHeight: boolean;
  onFullHeightChange: (fullHeight: boolean) => void;
}

const EmailModuleMessage: FC<EmailModuleMessageProps> = ({
  attachmentsFieldProps,
  otherRecipientsFieldProps,
  recipientsFieldProps,
  messageFieldProps,
  subjectFieldProps,
  errors,
  setError,
  clearErrors,
  documentation,
  sending,
  onPrepareSend,
  sendToMe,
  captchaToken,
  onCaptchaSuccess,
  onCaptchaExpire,
  turnstileRef,
  onRemoveDocument,
  setSelectedParticipants,
  selectedRecipients,
  otherRecipients,
  subject,
  outgoingBody,
  selectedEvent,
  eventInEmail,
  eventWhen,
  eventWhere,
  eventLink,
  eventLinkLoading,
  eventLinkFailed,
  onAddEventToEmail,
  onRemoveEventFromEmail,
  onDocumentsAttach,
  submitForm,
  onDiscardDraft,
  onSendToMeChange,
  handleOnWrapperClick,
  fullHeight,
  onFullHeightChange,
}) => {
  const { t, i18n } = useTranslation();
  const [documentsPopupOpen, setDocumentsPopupOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [deferConfirmShortcut, setDeferConfirmShortcut] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState<string[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const dragDepthRef = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const recipientsRef = useRef<RecipientsFieldHandle>(null);
  const editorRef = useRef<RichTextEditorHandle>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const [turnstileLayout, setTurnstileLayout] = useState<
    'row' | 'stacked' | 'tight'
  >('row');

  useLayoutEffect(() => {
    const footer = footerRef.current;
    if (!footer || !TURNSTILE_ENABLED) return undefined;

    const update = (width: number) => {
      const next = width < 340 ? 'tight' : width < 640 ? 'stacked' : 'row';
      setTurnstileLayout((current) => (current === next ? current : next));
    };

    update(footer.clientWidth);
    const observer = new ResizeObserver(() => update(footer.clientWidth));
    observer.observe(footer);
    return () => observer.disconnect();
  }, []);
  const currentUser = useSelector(getCurrentUserInCommunity);
  const community = useSelector(getCurrentCommunity);

  const isAdmin = hasCommunityOrGroupAdminPermissions(
    currentUser?.id ?? '',
    community,
  );
  const eligibleRecipients = getEligibleCommunityRecipients(community?.users);
  const maxCommunityRecipients = getMaxCommunityRecipients(community?.users);
  const attachments: File[] = attachmentsFieldProps.value || [];

  const recipientsError =
    getFieldError(errors, FIELDS.OTHER_RECIPIENTS)?.message ||
    getFieldError(errors, FIELDS.RECIPIENTS)?.message;
  const subjectError = getFieldError(errors, FIELDS.SUBJECT)?.message;
  const messageError = getFieldError(errors, FIELDS.MESSAGE)?.message;
  const attachmentsError = getFieldError(errors, FIELDS.ATTACHMENTS)?.message;

  const hasDraft =
    selectedRecipients.length > 0 ||
    !!otherRecipients.trim() ||
    !!subject.trim() ||
    !!(messageFieldProps.value || '').trim() ||
    attachments.length > 0 ||
    !!selectedEvent ||
    !sendToMe;
  const sendBlocked = sending || (TURNSTILE_ENABLED && !captchaToken);
  const sendHint = sendBlocked
    ? null
    : t('email.previewShortcut', { shortcut: `${MOD_KEY_LABEL}+Enter` });

  const handleOtherRecipientsChange = (value: string) => {
    otherRecipientsFieldProps.onChange(value);
  };

  const handleOtherRecipientsLimit = (exceeded: boolean) => {
    if (exceeded) {
      setError(FIELDS.OTHER_RECIPIENTS, {
        type: 'max',
        message: t('email.otherRecipientsMax', { max: MAX_OTHER_RECIPIENTS }),
      });
    }
  };

  const addFiles = (incoming: File[]) => {
    const fresh = incoming.filter(
      (file) => !attachments.some((existing) => existing.name === file.name),
    );
    if (!fresh.length) return;

    const merged = [...attachments, ...fresh];
    const totalBytes = merged.reduce((sum, file) => sum + (file?.size ?? 0), 0);

    if (totalBytes > MAX_EMAIL_ATTACHMENTS_BYTES) {
      setError(FIELDS.ATTACHMENTS, {
        type: 'validate',
        message: t('email.maxAttachmentsSize'),
      });
      return;
    }

    clearErrors?.(FIELDS.ATTACHMENTS);
    attachmentsFieldProps.onChange(merged);
  };

  const handleFileInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    addFiles(Array.from(event.target.files || []));
    event.target.value = '';
  };

  const handleRemoveAttachment = (file: File) => {
    clearErrors?.(FIELDS.ATTACHMENTS);
    onRemoveDocument(file);
  };

  const hasFiles = (event: DragEvent) =>
    Array.from(event.dataTransfer?.types || []).includes('Files');

  const handleDragEnter = (event: DragEvent<HTMLDivElement>) => {
    if (!hasFiles(event)) return;
    event.preventDefault();
    dragDepthRef.current += 1;
    setIsDragging(true);
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    if (!hasFiles(event)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    if (!hasFiles(event)) return;
    dragDepthRef.current = Math.max(dragDepthRef.current - 1, 0);
    if (dragDepthRef.current === 0) setIsDragging(false);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    if (!hasFiles(event)) return;
    event.preventDefault();
    dragDepthRef.current = 0;
    setIsDragging(false);
    addFiles(Array.from(event.dataTransfer.files || []));
  };

  const handleRequestSend = async (fromShortcut = false) => {
    if (sendBlocked || previewOpen) return;
    const valid = await onPrepareSend();
    if (!valid) return;
    setDeferConfirmShortcut(fromShortcut);
    setPreviewOpen(true);
  };

  const handleComposerKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      handleRequestSend(true);
    }
  };

  const handleConfirmSend = async () => {
    await submitForm();
    setPreviewOpen(false);
  };

  const handleDiscardDraft = () => {
    if (!hasDraft || sending) return;
    setPreviewOpen(false);
    setDocumentsPopupOpen(false);
    setSelectedDocuments([]);
    recipientsRef.current?.clear();
    onDiscardDraft();
  };

  const previewRecipients = [
    ...selectedRecipients
      .map((id) => (community?.users || []).find((user) => user.id === id))
      .filter(Boolean)
      .map((user) => ({
        name: getProfileName(user.profile || {}),
        detail: user.profile?.email || '',
      })),
    ...parseOtherRecipients(otherRecipients).map((email) => ({
      name: email,
      detail: '',
    })),
  ];

  const handleSubjectKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !event.metaKey && !event.ctrlKey) {
      event.preventDefault();
      editorRef.current?.focus();
    }
  };

  return (
    <StyledComposerSection $fullHeight={fullHeight}>
      {!isAdmin && (
        <GridGhostWrapper onClick={(e) => handleOnWrapperClick(e)} />
      )}
      <DocumentationPopup
        isOpen={documentsPopupOpen}
        data={documentation}
        selectedDocuments={selectedDocuments}
        groups={community.groups}
        setSelectedDocuments={setSelectedDocuments}
        onRemoveDocument={onRemoveDocument}
        onSubmit={onDocumentsAttach}
        closePopup={() => setDocumentsPopupOpen(false)}
      />
      <StyledComposer
        onKeyDown={handleComposerKeyDown}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {isDragging && (
          <StyledDropOverlay>
            <ComposerPaperclipIcon />
            {t('email.dropFiles')}
          </StyledDropOverlay>
        )}
        <StyledSheet>
          <StyledSheetInset>
            <StyledComposerHeader>
              <StyledComposerTitle>{t('email.newEmail')}</StyledComposerTitle>
              <StyledComposerHeaderActions>
                <StyledCopyToggle
                  type="button"
                  role="switch"
                  aria-checked={sendToMe}
                  active={sendToMe}
                  onClick={() => onSendToMeChange(!sendToMe)}
                >
                  <StyledCopyCheck active={sendToMe}>
                    <ComposerCheckIcon />
                  </StyledCopyCheck>
                  {t('email.sendCopy')}
                </StyledCopyToggle>
                <Tooltip
                  text={
                    fullHeight
                      ? t('email.showEvents')
                      : t('email.composeFullHeight')
                  }
                  variant="dark"
                  position="top-center"
                  noArrow
                >
                  <StyledHeaderIconButton
                    type="button"
                    aria-pressed={fullHeight}
                    aria-label={
                      fullHeight
                        ? t('email.showEvents')
                        : t('email.composeFullHeight')
                    }
                    onClick={() => onFullHeightChange(!fullHeight)}
                  >
                    {fullHeight ? (
                      <ComposerCollapseIcon />
                    ) : (
                      <ComposerExpandIcon />
                    )}
                  </StyledHeaderIconButton>
                </Tooltip>
              </StyledComposerHeaderActions>
            </StyledComposerHeader>

            <RecipientsField
              ref={recipientsRef}
              inputId={RECIPIENTS_INPUT_ID}
              users={eligibleRecipients}
              selectedIds={selectedRecipients}
              otherRecipients={otherRecipients}
              maxMembers={maxCommunityRecipients}
              error={recipientsError}
              onSelectedIdsChange={setSelectedParticipants}
              onOtherRecipientsChange={handleOtherRecipientsChange}
              onOtherRecipientsLimit={handleOtherRecipientsLimit}
              bindInput={(node) => {
                recipientsFieldProps.ref.current = node;
                otherRecipientsFieldProps.ref.current = node;
              }}
            />

            <StyledSubjectInput
              name={subjectFieldProps.name}
              ref={subjectFieldProps.ref}
              value={subjectFieldProps.value}
              onChange={subjectFieldProps.onChange}
              onBlur={subjectFieldProps.onBlur}
              autoComplete="off"
              placeholder={t('email.subjectPlaceholder')}
              aria-label={t('email.subject')}
              aria-invalid={!!subjectError}
              hasError={!!subjectError}
              onKeyDown={handleSubjectKeyDown}
            />
            {subjectError && (
              <StyledInlineError>{subjectError}</StyledInlineError>
            )}
          </StyledSheetInset>

          <StyledComposerBody>
            <StyledComposerScrollContent $fill={fullHeight}>
              {selectedEvent && (
                <EventBlock
                  title={selectedEvent.title}
                  when={eventWhen}
                  where={eventWhere}
                  accent={selectedEvent.eventType?.color}
                  included={eventInEmail}
                  url={eventLink}
                  linkLoading={eventLinkLoading}
                  linkFailed={eventLinkFailed}
                  onAdd={onAddEventToEmail}
                  onRemove={onRemoveEventFromEmail}
                />
              )}
              <RichTextEditor
                ref={editorRef}
                id={MESSAGE_EDITOR_ID}
                value={messageFieldProps.value}
                placeholder={t('email.messagePlaceholder')}
                hasError={!!messageError}
                fill={fullHeight}
                onChange={messageFieldProps.onChange}
                onBlur={messageFieldProps.onBlur}
                focusRef={messageFieldProps.ref}
              />
              {messageError && (
                <StyledInlineError>{messageError}</StyledInlineError>
              )}

              <Attachments
                files={attachments}
                maxBytes={MAX_EMAIL_ATTACHMENTS_BYTES}
                error={attachmentsError}
                onRemove={handleRemoveAttachment}
              />
              <FormatToolbar editorRef={messageFieldProps.ref} />
            </StyledComposerScrollContent>
          </StyledComposerBody>
        </StyledSheet>

        <StyledFooter ref={footerRef} data-layout={turnstileLayout}>
          <StyledFooterTools>
            <Tooltip
              text={t('documentation.attachFiles')}
              variant="dark"
              position="top-center"
              noArrow
            >
              <StyledIconButton
                type="button"
                aria-label={t('documentation.attachFiles')}
                onClick={() => fileInputRef.current?.click()}
              >
                <ComposerPaperclipIcon />
              </StyledIconButton>
            </Tooltip>
            <Tooltip
              text={t('email.attachCommunityDocs')}
              variant="dark"
              position="top-center"
              noArrow
            >
              <StyledIconButton
                type="button"
                aria-label={t('email.attachCommunityDocs')}
                onClick={() => setDocumentsPopupOpen(true)}
              >
                <ComposerDocumentIcon />
              </StyledIconButton>
            </Tooltip>
            <Tooltip
              text={t('email.addRecipients')}
              variant="dark"
              position="top-center"
              noArrow
            >
              <StyledIconButton
                type="button"
                aria-label={t('email.addRecipients')}
                onClick={() => recipientsRef.current?.focus()}
              >
                <ComposerUserPlusIcon />
              </StyledIconButton>
            </Tooltip>
            <StyledHiddenFileInput
              ref={fileInputRef}
              type="file"
              multiple
              tabIndex={-1}
              onChange={handleFileInputChange}
            />
          </StyledFooterTools>
          {TURNSTILE_ENABLED && (
            <StyledTurnstile
              ref={turnstileRef}
              siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
              language={i18n.language}
              onSuccess={onCaptchaSuccess}
              onExpire={onCaptchaExpire}
            />
          )}
          <StyledFooterActions>
            <Tooltip
              text={t('email.discardDraft')}
              variant="dark"
              position="top-center"
              noArrow
            >
              <StyledSendButtonWrapper>
                <StyledDiscardButton
                  type="button"
                  disabled={!hasDraft || sending}
                  aria-label={t('email.discardDraft')}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={handleDiscardDraft}
                >
                  <DeleteIcon width={16} height={16} />
                </StyledDiscardButton>
              </StyledSendButtonWrapper>
            </Tooltip>
            <Tooltip
              text={sendHint || ''}
              variant="dark"
              position="top-center"
              noArrow
              disabled={!sendHint}
            >
              <StyledSendButtonWrapper>
                <StyledSendButton
                  type="button"
                  disabled={sendBlocked}
                  aria-busy={sending}
                  onClick={() => handleRequestSend(false)}
                >
                  {sending ? (
                    <Loader type="button" size="22px" thickness={2} />
                  ) : (
                    <>
                      {t('auth.send')}
                      <ComposerSendIcon />
                    </>
                  )}
                </StyledSendButton>
              </StyledSendButtonWrapper>
            </Tooltip>
          </StyledFooterActions>
        </StyledFooter>
      </StyledComposer>
      <EmailPreview
        isOpen={previewOpen}
        sending={sending}
        confirmDisabled={TURNSTILE_ENABLED && !captchaToken}
        fromName={getProfileName(currentUser?.profile || {})}
        fromEmail={currentUser?.profile?.email}
        recipients={previewRecipients}
        subject={subject}
        body={outgoingBody}
        attachments={attachments.map((file) => ({
          name: file.name,
          size: file.size,
        }))}
        onClose={() => {
          if (!sending) setPreviewOpen(false);
        }}
        deferConfirmShortcut={deferConfirmShortcut}
        onConfirm={handleConfirmSend}
      />
    </StyledComposerSection>
  );
};

export default React.memo(EmailModuleMessage);
